import { useEffect, useRef } from 'react';

type Seed = {
  angle: number;
  baseX: number;
  baseY: number;
  depth: number;
  hasLetter: boolean;
  letter: string;
  phase: number;
  swaySpeed: number;
  swayAmp: number;
  windX: number;
  windY: number;
  spin: number;
  delay: number;
  dist: number;
};

type CoreLine = {
  angle: number;
  len: number;
  phase: number;
};

export default function DandelionHero() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);
  const wordmarkRef = useRef<HTMLHeadingElement | null>(null);
  const letterRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const letterRandRef = useRef<Array<{ delay: number; dx: number; dy: number; rot: number }>>([]);
  // Refs for the 5 UI blocks that must also blow away to the upper-right.
  const topLeftRef = useRef<HTMLDivElement | null>(null);
  const topRightRef = useRef<HTMLDivElement | null>(null);
  const scrollHintRef = useRef<HTMLDivElement | null>(null);
  const bottomLeftRef = useRef<HTMLDivElement | null>(null);
  const bottomRightRef = useRef<HTMLDivElement | null>(null);
  const progressRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const heroEl = heroRef.current;
    if (!canvas || !heroEl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W = 0;
    let H = 0;
    let DPR = 1;

    const resize = () => {
      DPR = window.devicePixelRatio || 1;
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const N = 2800;
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const seeds: Seed[] = [];

    const makeSeeds = (R: number) => {
      seeds.length = 0;
      for (let i = 0; i < N; i++) {
        // 2D sunflower / Fermat spiral -> perfectly circular puff
        const t = (i + 0.5) / N;
        const r = Math.sqrt(t);
        const theta = goldenAngle * i;
        const x = Math.cos(theta) * r;
        const y = Math.sin(theta) * r;

        // depth is faked so we can still color some as green/darker
        const depth = 1 - r;
        const radius = R * (0.35 + 0.75 * r);
        const hasLetter = Math.random() < 0.28;

        // wind blowing mostly to the right & slightly up, with per-seed jitter
        const windBase = -0.25; // -PI/2 = up, 0 = right; slightly upward-right
        const outAngle = windBase + (Math.random() - 0.5) * 0.9;
        const speedJitter = 0.9 + Math.random() * 1.1;

        seeds.push({
          angle: outAngle,
          baseX: x * radius,
          baseY: y * radius,
          depth,
          hasLetter,
          letter: LETTERS[Math.floor(Math.random() * LETTERS.length)],
          phase: Math.random() * Math.PI * 2,
          swaySpeed: 0.4 + Math.random() * 0.6,
          swayAmp: 2 + Math.random() * 4,
          windX: Math.cos(outAngle) * speedJitter,
          windY: Math.sin(outAngle) * speedJitter,
          spin: (Math.random() - 0.5) * 0.02,
          // wider delay range so the scatter unfolds gradually
          delay: Math.random() * 0.7,
          dist: 1400 + Math.random() * 1800,
        });
      }
    };

    const CORE_N = 46;
    const coreLines: CoreLine[] = Array.from({ length: CORE_N }, () => ({
      angle: Math.random() * Math.PI * 2,
      len: 24 + Math.random() * 40,
      phase: Math.random() * Math.PI * 2,
    }));

    const jitterPoint = (x: number, y: number, amt: number): [number, number] => [
      x + (Math.random() - 0.5) * amt,
      y + (Math.random() - 0.5) * amt,
    ];

    const drawScribbleCircle = (x: number, y: number, r: number) => {
      ctx.beginPath();
      const steps = 14;
      for (let i = 0; i <= steps; i++) {
        const a = (i / steps) * Math.PI * 2;
        const jr = r + (Math.random() - 0.5) * r * 0.15;
        const px = x + Math.cos(a) * jr;
        const py = y + Math.sin(a) * jr * 0.9;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.strokeStyle = 'rgba(70,150,60,0.85)';
      ctx.lineWidth = 1.6;
      ctx.stroke();
    };

    let time = 0;

    const render = () => {
      time += 0.016;
      const progress = progressRef.current;
      ctx.clearRect(0, 0, W, H);

      const cx = W * 0.5;
      const cy = H * 0.42;
      const R = Math.min(W, H) * 0.2;

      if (seeds.length === 0) makeSeeds(R);

      const globalDrift = {
        x: progress * progress * 260,
        y: -progress * 60,
      };

      const echoLayers = [
        { scale: 1, offset: 0, alphaMul: 1 },
        { scale: 0.72, offset: 520, alphaMul: 0.35 },
        { scale: 0.5, offset: 980, alphaMul: 0.18 },
      ];

      echoLayers.forEach((layer, li) => {
        const layerProgress = Math.min(1, Math.max(0, (progress - li * 0.18) * 1.4));
        if (layerProgress <= 0 && li > 0) return;

        const lcx = cx + globalDrift.x * (0.4 + li * 0.5) + layer.offset * layerProgress;
        const lcy = cy + globalDrift.y * (0.4 + li * 0.5) - layerProgress * 40;
        const baseAlpha =
          li === 0 ? 1 - progress * 0.15 : layer.alphaMul * (1 - layerProgress * 0.6);

        if (baseAlpha <= 0.01) return;

        if (li === 0) {
          const coreFade = Math.max(0, 1 - progress * 1.3);
          coreLines.forEach((c) => {
            const sway = Math.sin(time * 0.8 + c.phase) * 3;
            const ang = c.angle + sway * 0.01;
            const x2 = lcx + Math.cos(ang) * c.len;
            const y2 = lcy + Math.sin(ang) * c.len * 0.8;
            ctx.beginPath();
            ctx.moveTo(lcx, lcy);
            ctx.lineTo(x2, y2);
            ctx.strokeStyle = `rgba(60,160,55,${0.5 * coreFade})`;
            ctx.lineWidth = 1.8;
            ctx.stroke();
          });
        }

        seeds.forEach((s) => {
          const startDelay = s.delay;
          const span = Math.max(0.15, 1 - startDelay);
          const local = Math.min(1, Math.max(0, (layerProgress - startDelay) / span));
          // smoother, longer-tail easing so the scatter feels detailed
          const eased = local < 0.5
            ? 2 * local * local
            : 1 - Math.pow(-2 * local + 2, 3) / 2;

          const sway = Math.sin(time * s.swaySpeed + s.phase) * s.swayAmp * (1 - eased);
          let x = s.baseX + sway;
          let y =
            s.baseY +
            Math.cos(time * s.swaySpeed * 0.7 + s.phase) * s.swayAmp * 0.5 * (1 - eased);

          x += eased * s.windX * s.dist;
          y += eased * s.windY * s.dist;

          const px = lcx + x * layer.scale;
          const py = lcy + y * layer.scale;
          const alpha = baseAlpha * (1 - eased * 0.95);

          if (alpha <= 0.01) return;

          const green = s.depth > 0.6 && eased < 0.3;
          const color = green
            ? `rgba(90,180,70,${alpha * 0.8})`
            : `rgba(255,255,255,${alpha * (0.5 + s.depth * 0.5)})`;

          // Draw a very short filament near the seed tip (the tuft),
          // instead of a long ray from the center.
          const filamentLen = 6 + s.depth * 6;
          const dirX = s.baseX;
          const dirY = s.baseY;
          const dirLen = Math.hypot(dirX, dirY) || 1;
          const nx = (dirX / dirLen) * filamentLen * layer.scale;
          const ny = (dirY / dirLen) * filamentLen * layer.scale;

          ctx.beginPath();
          ctx.moveTo(px - nx, py - ny);
          ctx.lineTo(px, py);
          ctx.strokeStyle = color;
          ctx.lineWidth = 0.6;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(px, py, 1.3, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();

          if (s.hasLetter && li === 0) {
            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(eased * s.spin * 40);
            ctx.font = '10px sans-serif';
            ctx.fillStyle = `rgba(255,255,255,${alpha * 0.7})`;
            ctx.fillText(s.letter, 4, 4);
            ctx.restore();
          }
        });
      });

      const stemFade = Math.max(0, 1 - progress * 1.5);
      if (stemFade > 0.01) {
        ctx.beginPath();
        const sx = cx;
        const sy = cy + R * 0.55;
        ctx.moveTo(sx, sy);
        const stemLen = H * 0.45;
        const segs = 28;
        for (let i = 1; i <= segs; i++) {
          const py = sy + (stemLen / segs) * i;
          const wobble = Math.sin(i * 0.5 + time * 0.5) * 3;
          const [jx, jy] = jitterPoint(cx + wobble, py, 1.8);
          ctx.lineTo(jx, jy);
        }
        ctx.strokeStyle = `rgba(70,150,60,${0.9 * stemFade})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        drawScribbleCircle(cx - 8, sy + stemLen * 0.22, 18);
        drawScribbleCircle(cx + 16, sy + stemLen * 0.48, 16);
        drawScribbleCircle(cx - 12, sy + stemLen * 0.7, 14);
        drawScribbleCircle(cx + 6, sy + stemLen * 0.92, 18);
        ctx.globalAlpha = 1;
      }

      // Blow each letter of the wordmark toward the UPPER-RIGHT, one by one.
      const rands = letterRandRef.current;
      letterRefs.current.forEach((el, idx) => {
        if (!el) return;
        const r = rands[idx];
        if (!r) return;
        const span = Math.max(0.15, 1 - r.delay);
        const local = Math.min(1, Math.max(0, (progress - r.delay) / span));
        // ease-in-cubic: letters accelerate as the wind picks them up
        const eased = local * local * local;
        const tx = r.dx * eased;   // rightward
        const ty = -r.dy * eased;  // upward
        const rot = r.rot * eased;
        const opacity = Math.max(0, 1 - local * 1.15);
        const blur = eased * 8;
        el.style.transform = `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg)`;
        el.style.opacity = String(opacity);
        el.style.filter = `blur(${blur}px)`;
      });

      // Blow all the surrounding UI blocks toward the UPPER-RIGHT as well.
      // Each block has its own delay so they leave in a staggered wave.
      const blocks: Array<{
        el: HTMLDivElement | null;
        delay: number;
        dx: number;
        dy: number;
        rot: number;
      }> = [
        { el: topLeftRef.current, delay: 0.08, dx: 1400, dy: 500, rot: 6 },
        { el: topRightRef.current, delay: 0.18, dx: 900, dy: 400, rot: 8 },
        // scrollHint keeps its -50% Y centering; handled separately below
        { el: bottomLeftRef.current, delay: 0.3, dx: 1500, dy: 700, rot: 5 },
        { el: bottomRightRef.current, delay: 0.42, dx: 1000, dy: 600, rot: 7 },
      ];
      blocks.forEach((b) => {
        if (!b.el) return;
        const span = Math.max(0.15, 1 - b.delay);
        const local = Math.min(1, Math.max(0, (progress - b.delay) / span));
        const eased = local * local * local;
        const tx = b.dx * eased;
        const ty = -b.dy * eased;
        const rot = b.rot * eased;
        const opacity = Math.max(0, 1 - local * 1.25);
        const blur = eased * 6;
        b.el.style.transform = `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg)`;
        b.el.style.opacity = String(opacity);
        b.el.style.filter = `blur(${blur}px)`;
      });

      // Scroll hint (vertically centered) — preserve its -50% Y centering while blowing to upper-right.
      if (scrollHintRef.current) {
        const delay = 0.24;
        const span = Math.max(0.15, 1 - delay);
        const local = Math.min(1, Math.max(0, (progress - delay) / span));
        const eased = local * local * local;
        const tx = 700 * eased;
        const upPx = 350 * eased;
        const opacity = Math.max(0, 1 - local * 1.25);
        const blur = eased * 6;
        scrollHintRef.current.style.transform = `translate(${tx}px, calc(-50% - ${upPx}px)) rotate(${4 * eased}deg)`;
        scrollHintRef.current.style.opacity = String(opacity);
        scrollHintRef.current.style.filter = `blur(${blur}px)`;
      }

      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    const onScroll = () => {
      if (!heroEl) return;
      const rect = heroEl.getBoundingClientRect();
      const scrolled = -rect.top;
      const max = heroEl.offsetHeight - window.innerHeight;
      progressRef.current = Math.min(1, Math.max(0, scrolled / max));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="hero relative w-full bg-black"
      style={{ height: '500vh' }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <canvas
          ref={canvasRef}
          id="dandelion"
          className="absolute inset-0 block h-full w-full"
        />

        {/* Top-left small tag + description */}
        <div
          ref={topLeftRef}
          className="pointer-events-none absolute left-8 top-28 z-[3] max-w-[320px] text-white will-change-transform md:left-12"
          style={{ transition: 'filter 120ms linear' }}
        >
          <div className="mb-4 flex items-center gap-2 text-[10px] tracking-[3px] text-white/50">
            <span className="inline-block h-px w-6 bg-white/40"></span>
            <span>DANDELION STUDIO</span>
          </div>
          <p className="text-[13px] leading-[1.75] text-white/70">
            A quiet visual studio for stories that drift softly on the wind,
            crafted with warmth, patience, and a bit of poetry.
          </p>
        </div>

        {/* Top-right CTA */}
        <div
          ref={topRightRef}
          className="pointer-events-none absolute right-8 top-28 z-[3] will-change-transform md:right-12"
          style={{ transition: 'filter 120ms linear' }}
        >
          <button
            type="button"
            className="pointer-events-auto group flex cursor-pointer items-center gap-3 whitespace-nowrap rounded-full bg-white pl-6 pr-2 py-2 text-[12px] tracking-[2px] text-black transition-transform duration-300 hover:-translate-y-0.5"
          >
            BOOK A SESSION
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-white">
              <i className="ri-arrow-right-up-line text-[14px]"></i>
            </span>
          </button>
        </div>

        {/* Right-side rotated scroll indicator */}
        <div
          ref={scrollHintRef}
          className="pointer-events-none absolute right-6 top-1/2 z-[3] will-change-transform md:right-10"
          style={{ transform: 'translate(0, -50%)', transition: 'filter 120ms linear' }}
        >
          <div className="flex items-center gap-3 rotate-90 origin-right text-[10px] tracking-[4px] text-white/40">
            <span>SCROLL</span>
            <span className="inline-block h-px w-10 bg-white/30"></span>
          </div>
        </div>

        {/* Bottom-left small copy */}
        <div
          ref={bottomLeftRef}
          className="pointer-events-none absolute bottom-10 left-8 z-[3] max-w-[280px] text-white will-change-transform md:left-12 md:bottom-14"
          style={{ transition: 'filter 120ms linear' }}
        >
          <p className="text-[12px] leading-[1.7] text-white/55">
            Scroll gently — a single breath sets a thousand tiny seeds
            travelling toward the next quiet horizon.
          </p>
        </div>

        {/* Bottom-right meta */}
        <div
          ref={bottomRightRef}
          className="pointer-events-none absolute bottom-10 right-8 z-[3] text-right text-white will-change-transform md:right-12 md:bottom-14"
          style={{ transition: 'filter 120ms linear' }}
        >
          <div className="text-[10px] tracking-[3px] text-white/40">EST. 2024</div>
          <div className="mt-1 text-[10px] tracking-[3px] text-white/40">CHAPTER 01 / GENTLE BREATH</div>
        </div>

        {/* Huge brand wordmark — each letter blown to the upper-left */}
        <div className="pointer-events-none absolute inset-x-0 bottom-24 z-[2] flex justify-center md:bottom-28">
          <h1
            ref={wordmarkRef}
            className="font-display font-semibold leading-none tracking-[-0.04em] text-white/95"
            style={{ fontSize: 'clamp(90px, 16vw, 220px)' }}
            aria-label="Dandelion"
          >
            {'Dandelion'.split('').map((ch, i) => (
              <span
                key={i}
                ref={(el) => {
                  letterRefs.current[i] = el;
                  if (!letterRandRef.current[i]) {
                    // Left-to-right letters get progressively LATER delays so
                    // 'D' flies off first and 'n' flies off last, like a wind
                    // sweeping across the word from the left.
                    const total = 'Dandelion'.length;
                    const base = i / (total - 1); // 0 .. 1
                    letterRandRef.current[i] = {
                      delay: 0.05 + base * 0.55 + Math.random() * 0.08,
                      dx: 600 + Math.random() * 500, // horizontal blow to the RIGHT
                      dy: 300 + Math.random() * 250, // upward
                      rot: 20 + (Math.random() - 0.5) * 30,
                    };
                  }
                }}
                className="inline-block will-change-transform"
                style={{ transition: 'filter 120ms linear' }}
              >
                {ch}
              </span>
            ))}
          </h1>
        </div>

      </div>
    </section>
  );
}